import React, { useEffect, useState } from 'react';

import HomeApprovalsPanel from './HomeApprovalsPanel';
import { ATTENTION_APPROVALS } from './homeAttention';
import HomeSection from './HomeSection';
import {
    TYPE_CONTRACTS,
    TYPE_DECISIONS,
    TYPE_EVALUATIONS,
    TYPE_ISSUES,
    TYPE_OPEN_ACTIONS,
    TYPE_OPPORTUNITIES,
    TYPE_PROCUREMENTS,
    TYPE_RISKS,
} from './homeSectionSummary';
import ContractSectionTable from './sections/ContractSectionTable';
import DecisionSectionTable from './sections/DecisionSectionTable';
import EvaluationSectionTable from './sections/EvaluationSectionTable';
import IssueSectionTable from './sections/IssueSectionTable';
import OpenActionsTable from './sections/OpenActionsTable';
import OpportunitySectionTable from './sections/OpportunitySectionTable';
import ProcurementSectionTable from './sections/ProcurementSectionTable';
import RiskSectionTable from './sections/RiskSectionTable';
import WorkDetailModal from './work/WorkDetailModal';

import useModal from 'Hooks/useModal';

/**
 * The one detail panel, and the pop-up its rows open.
 *
 * ONE LIST AT A TIME, IN ONE PLACE. The page used to stack up to eight
 * independently-expandable sections, each with its own `+`; a user comparing
 * two of them scrolled past the tiles to find the second, and a user who
 * expanded four had lost the row of tiles entirely. Now a tile or an attention
 * card chooses what the single panel below them holds, and the panel never
 * moves.
 *
 * A TILE RENDERS AT ZERO BUT THE PANEL IS STILL REAL. Opening an empty list
 * shows an empty list, which is the honest answer to "what are my risks" for a
 * user who has none — see HomeTiles for why the tile itself no longer
 * disappears.
 *
 * PRESENCE STILL MEANS CAPABILITY. `openType` is only ever set to a type whose
 * count key arrived, because that is the only kind of tile there is to click;
 * the default below picks the first such type rather than assuming
 * procurements.
 */
const TABLES = {
    [TYPE_OPEN_ACTIONS]: OpenActionsTable,
    [TYPE_RISKS]: RiskSectionTable,
    [TYPE_ISSUES]: IssueSectionTable,
    [TYPE_DECISIONS]: DecisionSectionTable,
    /* Neither has a tile any more, and neither can be reached from this panel
       today — no tile, attention card or default-open pick ever sets
       `openType` to one of these two, since both are absent from
       SECTION_TYPES. The section endpoint still serves them and the table
       components still exist, kept deliberately so a future restoration is a
       SECTION_TYPES change, not new components. Left in TABLES so that
       restoration doesn't also need to rebuild this map. */
    [TYPE_OPPORTUNITIES]: OpportunitySectionTable,
    [TYPE_PROCUREMENTS]: ProcurementSectionTable,
    [TYPE_CONTRACTS]: ContractSectionTable,
    [TYPE_EVALUATIONS]: EvaluationSectionTable,
};

export default function HomeSections({
    openType,
    counts = {},
    deltas = {},
    sectionUrlTemplate,
    sectionTypePlaceholder,
    detailUrlTemplate,
    detailStatusUrlTemplate,
    detailTypePlaceholder,
    detailIdPlaceholder,
    approvalsQueueUrl,
    onViewAllApprovals,
    onApprovalActioned,
    approvalsRefreshToken,
    myViews,
}) {
    /* ONE POP-UP FOR THE WHOLE PANEL, mounted here rather than inside
       HomeSection. Several sections each mounting their own would put several
       elements with the same DOM id on the page — the collision
       ApprovalDetailModal documents, made worse by HomeTabs keeping a visited
       tab mounted. */
    const detailModal = useModal();

    /* The row the pop-up is open on: its detail type and its id, which is all
       the server needs. One at a time, so a second open cannot leave the first
       record's payload behind. `section` is carried only to know which list to
       re-read after a write. */
    const [target, setTarget] = useState(null);

    /* Bumped per type, so a write refreshes the list the row came from. */
    const [refreshTokens, setRefreshTokens] = useState({});

    useEffect(() => {
        target ? detailModal.show() : detailModal.hide();
    }, [target]);

    const openRow = (section, row) => setTarget({ section, detailType: row.detail_type, id: row.id });

    const close = () => setTarget(null);

    const afterAction = () => {
        const section = target?.section;

        close();

        if (section) {
            setRefreshTokens((tokens) => ({ ...tokens, [section]: (tokens[section] ?? 0) + 1 }));
        }
    };

    if (!openType) {
        return null;
    }

    if (openType === ATTENTION_APPROVALS) {
        return (
            <HomeApprovalsPanel
                approvalsQueueUrl={approvalsQueueUrl}
                pendingTotal={counts.approvals}
                onViewAll={onViewAllApprovals}
                onQueueChange={onApprovalActioned}
                refreshToken={approvalsRefreshToken}
                myViews={myViews}
            />
        );
    }

    const Table = TABLES[openType];

    if (!Table) {
        return null;
    }

    return (
        <>
            <HomeSection
                /* Keyed on the type so switching list remounts rather than
                   showing the previous one's rows under the new heading while
                   the fetch is in flight. */
                key={openType}
                type={openType}
                count={counts[openType] ?? 0}
                deltas={deltas}
                sectionUrl={sectionUrlTemplate.replace(sectionTypePlaceholder, openType)}
                refreshToken={refreshTokens[openType] ?? 0}
            >
                {/* The table is built here, so the click handler is closed over
                    rather than threaded through HomeSection — which has no use
                    for it and would only be another place for it to go missing.

                    `row.detail_type` IS ECHOED, NEVER DERIVED. The two
                    open-action sources share this list and one row shape but
                    live in separate tables, so only the server's own field says
                    which table an id names. */}
                {(rows) => <Table rows={rows} onRowClick={(row) => openRow(openType, row)} />}
            </HomeSection>

            <WorkDetailModal
                modal={detailModal}
                target={target}
                detailUrlTemplate={detailUrlTemplate}
                statusUrlTemplate={detailStatusUrlTemplate}
                detailTypePlaceholder={detailTypePlaceholder}
                idPlaceholder={detailIdPlaceholder}
                onClose={close}
                onActioned={afterAction}
            />
        </>
    );
}
